import { useTranslation } from 'react-i18next';

import type { SearchResult } from '../../types';

const DAY_MS = 86_400_000;
const SEPARATOR = ' · ';

/** A row's two lines, per record type, in the active language. */
export function useResultText() {
  const { t, i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);
  // `auto` says "yesterday", not "1 day ago", as the design does.
  const relative = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
  const devices = (count: number) =>
    t('Search.Detail.DeviceCount', { count, formatted: number.format(count) });

  return (result: SearchResult): { title: string; detail: string } => {
    switch (result.type) {
      case 'DEVICE':
        // A device not enrolled has no service or plan; its state is always there. The state
        // reads as the platform sends it, as the registry's STATE badge does.
        return {
          title: result.identifier,
          detail: [
            result.service && t(`Devices.Services.${result.service}`),
            result.plan,
            result.state,
          ]
            .filter(Boolean)
            .join(SEPARATOR),
        };
      case 'WORKFLOW': {
        const days = Math.round((Date.now() - result.updatedAt.getTime()) / DAY_MS);
        return {
          title: result.name,
          detail: t('Search.Detail.Workflow', {
            devices: devices(result.deviceCount),
            updated: relative.format(-days, 'day'),
          }),
        };
      }
      case 'PLAN':
        return {
          title: result.name,
          detail: result.service
            ? t('Search.Detail.Plan', {
                service: t(`Devices.Services.${result.service}`),
                devices: devices(result.deviceCount),
              })
            : devices(result.deviceCount),
        };
      case 'CONFIGURATION': {
        const section = t('Search.Detail.Configuration', {
          section: t(`Search.Areas.${result.area}`),
        });
        // A user's email shows beside the area, so a match on it is highlighted.
        return {
          title: result.name,
          detail: result.email ? `${section}${SEPARATOR}${result.email}` : section,
        };
      }
    }
  };
}
